import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { TrustBadges } from '../components/TrustBadges';
import { ServiceCard } from '../components/ServiceCard';
import { FAQ } from '../components/FAQ';
import { Testimonials } from '../components/Testimonials';
import { GraduationCap, TrendingUp, BookOpen, Briefcase, MessageCircle } from 'lucide-react';
export function HomePage() {
  return <Layout data-id="element-245">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-20 md:py-28 overflow-hidden" data-id="element-246">
        <div className="absolute inset-0 bg-blue-900/20 z-0" data-id="element-247"></div>
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" data-id="element-248"></div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl" data-id="element-249"></div>

        <Container className="relative z-10" data-id="element-250">
          <div className="max-w-3xl mx-auto text-center" data-id="element-251">
            <div className="inline-flex items-center gap-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-1.5 mb-6" data-id="element-252">
              <GraduationCap className="h-4 w-4 text-blue-300" data-id="element-253" />
              <span className="text-sm font-medium text-blue-200" data-id="element-254">
                Financial Education & Consulting
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-white" data-id="element-255">
              Credit Education & Financial Consulting to help you understand
              your credit profile.
            </h1>
            <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-256">
              Education-first guidance and strategic consulting based on your
              financial goals. We teach you how credit works — clearly,
              transparently, and professionally.
            </p>
            <p className="mt-2 text-sm text-slate-400" data-id="element-257">
              Secure • Confidential • Education-First
            </p>
          </div>
        </Container>
      </section>

      <TrustBadges data-id="element-258" />

      {/* Services Overview */}
      <section className="py-20 bg-white" data-id="element-259">
        <Container data-id="element-260">
          <div className="text-center mb-16" data-id="element-261">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-262">
              Educational & Consulting Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-263">
              We provide the knowledge, analysis, and strategy you need to make
              informed financial decisions on your own.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-264">
            <ServiceCard title="Credit Profile Analysis" description="An educational review of your credit profile to help you understand the factors that shape it." icon={BookOpen} features={['Profile Education', 'Factor Explanation', 'Understanding Your Report']} link="/services" data-id="element-265" />
            <ServiceCard title="Credit Education" description="Learn how credit scoring models work and the principles of building a strong history responsibly." icon={GraduationCap} features={['Scoring Models', 'Utilization Strategy', 'History Building']} link="/services" data-id="element-266" />
            <ServiceCard title="Financial Literacy" description="Educational guidance on budgeting, saving, and responsible debt management strategies." icon={TrendingUp} features={['Budgeting Basics', 'Debt Strategy', 'Savings Goals']} link="/services" data-id="element-267" />
            <ServiceCard title="Readiness Consulting" description="Strategic consulting to help you understand what lenders look for ahead of major milestones." icon={Briefcase} features={['Mortgage Readiness', 'Business Credit', 'Application Education']} link="/services" data-id="element-268" />
          </div>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-id="element-269">
        <Container data-id="element-270">
          <div className="text-center mb-16" data-id="element-271">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-272">
              How We Work With You
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-273">
              Our process is transparent, collaborative, and focused on teaching
              you to succeed long-term.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative" data-id="element-274">
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-slate-200 -z-10 transform -translate-y-1/2" data-id="element-275"></div>

            <div className="text-center relative bg-slate-50 p-4" data-id="element-276">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg" data-id="element-277">
                1
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-278">Reach Out</h3>
              <p className="text-slate-600" data-id="element-279">
                Call or email us to tell us about your goals and what you'd like
                to learn.
              </p>
            </div>
            <div className="text-center relative bg-slate-50 p-4" data-id="element-280">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg" data-id="element-281">
                2
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-282">Consultation</h3>
              <p className="text-slate-600" data-id="element-283">
                Meet with a specialist who will educate you on your profile and
                discuss strategies tailored to you.
              </p>
            </div>
            <div className="text-center relative bg-slate-50 p-4" data-id="element-284">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg" data-id="element-285">
                3
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-286">Education Plan</h3>
              <p className="text-slate-600" data-id="element-287">
                Receive a personalized educational roadmap with strategies you
                can choose to apply yourself.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-288">
            <a href="https://wa.me/17863763985" target="_blank" rel="noopener noreferrer" data-id="element-289">
              <Button size="lg" data-id="element-290">
                <MessageCircle className="h-5 w-5 mr-2" data-id="element-291" />
                Contact Us
              </Button>
            </a>
          </div>
        </Container>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white" data-id="element-292">
        <Container data-id="element-293">
          <div className="text-center mb-16" data-id="element-294">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-295">
              Client Experiences
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-296">
              Hear from individuals who have gained clarity and confidence
              through our educational consulting.
            </p>
          </div>
          <Testimonials data-id="element-297" />
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50 border-t border-slate-200" data-id="element-298">
        <Container data-id="element-299">
          <div className="text-center mb-12" data-id="element-300">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-301">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-302">
              Common questions about our educational services and approach.
            </p>
          </div>
          <FAQ data-id="element-303" />
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#7B2FE0] text-white" data-id="element-304">
        <Container data-id="element-305">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-306">
            <div className="max-w-2xl" data-id="element-307">
              <h2 className="text-3xl font-bold mb-4 text-white" data-id="element-308">
                Ready to better understand your finances?
              </h2>
              <p className="text-purple-100 text-lg" data-id="element-309">
                Contact us today for educational guidance and strategic
                consulting.
              </p>
            </div>
            <div className="flex-shrink-0" data-id="element-310">
              <a href="https://wa.me/17863763985" target="_blank" rel="noopener noreferrer" data-id="element-311">
                <Button size="lg" className="w-full bg-blue-600 text-white hover:bg-blue-700 shadow-lg border-none" data-id="element-312">
                  <MessageCircle className="h-5 w-5 mr-2" data-id="element-313" />
                  Contact Us
                </Button>
              </a>
            </div>
          </div>
        </Container>
      </section>
    </Layout>;
}